/**
 * Loads the organisation's responsibility configuration (RESP1).
 *
 * One request, all 13 areas. Used by the settings page and — with
 * `enabled` — by the task create form, where it must never block or
 * break task creation: a failure leaves `areas` empty, which the caller
 * reads as "no defaults to suggest", not as an error worth stopping for.
 *
 * Race protection is a generation counter rather than AbortController:
 * the concern here is a STALE response landing after a newer one (or
 * after the consumer stopped caring), and ignoring it by generation is
 * both simpler and immune to a fetch that already resolved.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { fetchResponsibilityAreas } from "../../../api/adminData";

export default function useResponsibilityAreas({ enabled = true } = {}) {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const generation = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!enabled) return;
    const gen = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchResponsibilityAreas();
      // A newer load started, or nobody is listening any more.
      if (gen !== generation.current || !mounted.current) return;
      setAreas(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      if (gen !== generation.current || !mounted.current) return;
      setError(err?.message || "Bereiche konnten nicht geladen werden.");
      // Deliberately NOT cleared: a caller that already has data keeps
      // showing it rather than blanking the page on a refresh failure.
    } finally {
      if (gen === generation.current && mounted.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);

  /** `{code: area}` — what the task form resolves a suggestion through. */
  const byCategory = areas.reduce((acc, a) => {
    if (a?.activity_category) acc[a.activity_category] = a;
    return acc;
  }, {});

  return { areas, byCategory, loading, error, refresh: load };
}
